import React, { useEffect, useRef, useState } from 'react'
import { Head, Link, router, usePage } from '@inertiajs/react'
import {
  Container, Typography, Box, Button, Paper, Grid, Chip, Divider, Stack,
  TextField, MenuItem, IconButton, Card, Avatar
} from '@mui/material'
import LocationsMap from '../../components/LocationsMap'
import GroupChip from '../../components/GroupChip'
import GroupsIcon from '@mui/icons-material/Groups'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import PlaceIcon from '@mui/icons-material/Place'
import PhoneIcon from '@mui/icons-material/Phone'
import LanguageIcon from '@mui/icons-material/Language'
import SaveIcon from '@mui/icons-material/Save'
import CloseIcon from '@mui/icons-material/Close'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder'
import FavoriteIcon from '@mui/icons-material/Favorite'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlined'
import { toggleWishlist, toggleVisit, InteractionState } from '../../lib/locationInteractions'
import { WishlistSummary } from '../../lib/wishlists'
import WishlistMenu from '../../components/WishlistMenu'

interface GroupRef {
  id: number
  name: string
}

interface GroupVisit {
  id: number
  group: {
    id: number
    name: string
    company: string
  }
  visited_at: string | null
  source_type: string | null
  source_title: string | null
  source_url: string | null
  notes: string | null
}

interface Author {
  id: number
  slug: string
  name: string
  avatar_url: string | null
}

interface Location {
  id: number
  slug: string
  name: string
  address: string | null
  street_address: string | null
  district: string | null
  city: string | null
  region: string | null
  country: string | null
  postal_code: string | null
  latitude: number | null
  longitude: number | null
  phone: string | null
  opening_hours: Record<string, { open: string; close: string }> | null
  google_place_id: string | null
  category: string | null
  description: string | null
  website_url: string | null
  image_url: string | null
  groups: GroupRef[]
  group_visits: GroupVisit[]
  author: Author | null
  created_at: string
  updated_at: string
}

interface InteractionProps {
  wishlisted: boolean
  visited: boolean
  wishlisted_at: string | null
  visited_at: string | null
}

interface Props {
  location: Location
  interaction: InteractionProps | null
}

interface SharedProps {
  google_maps_api_key?: string
  current_user?: { id: number } | null
  wishlists_summary?: WishlistSummary[]
}

const categoryLabels: Record<string, string> = {
  restaurant: 'Restaurant',
  cafe: 'Cafe',
  filming_location: 'Filming Location',
  practice_studio: 'Practice Studio',
  entertainment_venue: 'Entertainment Venue',
  shop: 'Shop',
  landmark: 'Landmark',
  other: 'Other',
}

const sourceTypeLabels: Record<string, string> = {
  youtube_video: 'YouTube Video',
  social_media: 'Social Media',
  spotted_in_person: 'Spotted in Person',
  news_article: 'News Article',
  fan_report: 'Fan Report',
  other: 'Other',
}

const dayOrder = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']

export default function LocationShow({ location, interaction }: Props) {
  const sharedProps = usePage<SharedProps>().props
  const googleMapsApiKey = sharedProps.google_maps_api_key || ''
  const signedIn = !!sharedProps.current_user
  const [editingVisitId, setEditingVisitId] = useState<number | null>(null)
  const [editForm, setEditForm] = useState({
    visited_at: '',
    source_type: '',
    source_title: '',
    source_url: '',
    notes: '',
  })
  const [wishlisted, setWishlisted] = useState(interaction?.wishlisted ?? false)
  const [visitedFlag, setVisitedFlag] = useState(interaction?.visited ?? false)
  const [busyWish, setBusyWish] = useState(false)
  const [busyVisit, setBusyVisit] = useState(false)
  const [wishlistMenuOpen, setWishlistMenuOpen] = useState(false)
  const [localWishlists, setLocalWishlists] = useState<WishlistSummary[]>(sharedProps.wishlists_summary || [])
  const wishlistAnchorRef = useRef<HTMLButtonElement>(null)
  useEffect(() => { setLocalWishlists(sharedProps.wishlists_summary || []) }, [sharedProps.wishlists_summary])

  const applyResult = (result: InteractionState | null, fallbackKey: 'wish' | 'visit') => {
    if (result) {
      setWishlisted(result.wishlisted)
      setVisitedFlag(result.visited)
    } else if (fallbackKey === 'wish') {
      setWishlisted((v) => !v)
    } else {
      setVisitedFlag((v) => !v)
    }
  }

  const performToggleWish = async () => {
    if (!signedIn || busyWish) return
    const next = !wishlisted
    setWishlisted(next); setBusyWish(true)
    applyResult(await toggleWishlist(location.id, next), 'wish')
    setBusyWish(false)
  }

  const onToggleVisited = async () => {
    if (!signedIn || busyVisit) return
    const next = !visitedFlag
    setVisitedFlag(next); setBusyVisit(true)
    applyResult(await toggleVisit(location.id, next), 'visit')
    setBusyVisit(false)
  }

  const handleDelete = () => {
    if (confirm(`Are you sure you want to delete "${location.name}"?`)) {
      router.delete(`/locations/${location.slug || location.id}`)
    }
  }

  const startEditing = (visit: GroupVisit) => {
    setEditingVisitId(visit.id)
    setEditForm({
      visited_at: visit.visited_at || '',
      source_type: visit.source_type || '',
      source_title: visit.source_title || '',
      source_url: visit.source_url || '',
      notes: visit.notes || '',
    })
  }

  const cancelEditing = () => {
    setEditingVisitId(null)
  }

  const saveVisitDetails = (visitId: number) => {
    router.patch(`/locations/${location.slug || location.id}/group_locations/${visitId}`, {
      group_location: editForm,
    }, {
      onSuccess: () => setEditingVisitId(null),
    })
  }

  const removeGroup = async (groupId: number) => {
    if (!confirm('Remove this group from the location?')) return
    try {
      const csrfMeta = document.querySelector('meta[name="csrf-token"]')
      const csrfToken = csrfMeta?.getAttribute('content') || ''
      await fetch(`/api/v1/groups/${groupId}/remove_location/${location.id}`, {
        method: 'DELETE',
        headers: { 'X-CSRF-Token': csrfToken },
      })
      router.reload()
    } catch {
      // silently fail
    }
  }

  return (
    <>
      <Head title={location.name} />
      <Container maxWidth="xl" sx={{ py: { xs: 3, md: 4 }, px: { xs: 2, md: 4 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
          <Button
            component={Link}
            href="/locations"
            startIcon={<ArrowBackIcon />}
          >
            Back to Locations
          </Button>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Button
              component={Link}
              href={`/locations/${location.slug || location.id}/edit`}
              variant="contained"
              startIcon={<EditIcon />}
            >
              Edit
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleDelete}
            >
              Delete
            </Button>
          </Box>
        </Box>

        <Card sx={{ overflow: 'hidden', mb: 3 }}>
          {location.image_url && (
            <Box
              sx={{
                width: '100%',
                height: { xs: 200, md: 280 },
                backgroundImage: `url(${location.image_url})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            />
          )}
          <Box sx={{ p: { xs: 2.5, md: 3 } }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 1.5 }}>
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography variant="h5" sx={{ mb: 1 }}>{location.name}</Typography>
                <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', gap: 0.75, mb: 1.5 }}>
                  {location.category && (
                    <Chip label={categoryLabels[location.category] || location.category} size="small" />
                  )}
                </Stack>
              </Box>
              {signedIn && (
                <Stack direction="row" spacing={1} sx={{ flexShrink: 0, alignItems: 'center' }}>
                  <Button
                    onClick={onToggleVisited}
                    disabled={busyVisit}
                    variant={visitedFlag ? 'contained' : 'outlined'}
                    color={visitedFlag ? 'success' : 'inherit'}
                    startIcon={visitedFlag ? <CheckCircleIcon /> : <CheckCircleOutlineIcon />}
                  >
                    {visitedFlag ? 'Visited' : 'Mark visited'}
                  </Button>
                  <Button
                    ref={wishlistAnchorRef}
                    onClick={() => setWishlistMenuOpen(true)}
                    disabled={busyWish}
                    variant={wishlisted ? 'contained' : 'outlined'}
                    startIcon={wishlisted ? <FavoriteIcon /> : <FavoriteBorderIcon />}
                    aria-label={wishlisted ? 'saved, manage lists' : 'save to list'}
                  >
                    {wishlisted ? 'Saved' : 'Save'}
                  </Button>
                </Stack>
              )}
            </Box>
          </Box>
        </Card>

        <Paper sx={{ p: 4 }}>

          {location.description && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {location.description}
            </Typography>
          )}

          <Divider sx={{ my: 3 }} />

          <Grid container spacing={3}>
            {location.address && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <PlaceIcon sx={{ fontSize: 18, mt: 0.25 }} color="action" />
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block' }}>Address</Typography>
                    <Typography variant="body2">{location.address}</Typography>
                    {location.latitude && location.longitude && (
                      <Typography variant="caption" color="text.disabled">
                        {location.latitude.toFixed(4)}, {location.longitude.toFixed(4)}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Grid>
            )}

            {location.phone && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <PhoneIcon sx={{ fontSize: 18, mt: 0.25 }} color="action" />
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block' }}>Phone</Typography>
                    <Typography variant="body2">{location.phone}</Typography>
                  </Box>
                </Box>
              </Grid>
            )}

            {location.website_url && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <LanguageIcon sx={{ fontSize: 18, mt: 0.25 }} color="action" />
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block' }}>Website</Typography>
                    <Typography
                      variant="body2"
                      component="a"
                      href={location.website_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ wordBreak: 'break-all' }}
                    >
                      {location.website_url}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            )}
          </Grid>

          {location.latitude && location.longitude && (
            <Box sx={{ mt: 3 }}>
              <LocationsMap
                apiKey={googleMapsApiKey}
                locations={[{
                  id: location.id,
                  name: location.name,
                  address: location.address,
                  category: location.category,
                  latitude: location.latitude,
                  longitude: location.longitude,
                  google_place_id: location.google_place_id,
                }]}
                height={300}
              />
            </Box>
          )}

          {location.opening_hours && Object.keys(location.opening_hours).length > 0 && (
            <>
              <Divider sx={{ my: 3 }} />
              <Typography variant="h6" sx={{ mb: 1 }}>Opening Hours</Typography>
              {dayOrder.map((day) => {
                const hours = location.opening_hours?.[day]
                if (!hours) return null
                return (
                  <Box key={day} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25, maxWidth: 280 }}>
                    <Typography variant="caption" sx={{ textTransform: 'capitalize' }}>
                      {day}
                    </Typography>
                    <Typography variant="caption">
                      {hours.open} - {hours.close}
                    </Typography>
                  </Box>
                )
              })}
            </>
          )}

          <Divider sx={{ my: 3 }} />

          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 2 }}>
            <GroupsIcon color="action" />
            <Typography variant="h6">Group Visits ({location.group_visits?.length ?? 0})</Typography>
          </Stack>

          {location.group_visits && location.group_visits.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center', fontStyle: 'italic' }}>
              No groups associated yet. Edit this location to add groups.
            </Typography>
          )}

          {location.group_visits && location.group_visits.map((visit) => (
            <Paper key={visit.id} variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                  <GroupsIcon sx={{ fontSize: 18 }} color="action" />
                  <GroupChip group={visit.group} />
                </Stack>
                <Stack direction="row" spacing={0.5}>
                  {editingVisitId !== visit.id && (
                    <IconButton size="small" onClick={() => startEditing(visit)} aria-label="edit visit">
                      <EditIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  )}
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => removeGroup(visit.group.id)}
                    aria-label="remove group"
                  >
                    <DeleteIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Stack>
              </Box>

              {editingVisitId === visit.id ? (
                <Box sx={{ mt: 1 }}>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        label="Visit Date"
                        type="date"
                        value={editForm.visited_at}
                        onChange={(e) => setEditForm({ ...editForm, visited_at: e.target.value })}
                        fullWidth
                        slotProps={{ inputLabel: { shrink: true } }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        label="Source"
                        select
                        value={editForm.source_type}
                        onChange={(e) => {
                          const newType = e.target.value
                          setEditForm({
                            ...editForm,
                            source_type: newType,
                            source_title: newType === 'youtube_video' ? editForm.source_title : '',
                          })
                        }}
                        fullWidth
                      >
                        <MenuItem value="">None</MenuItem>
                        {Object.entries(sourceTypeLabels).map(([value, label]) => (
                          <MenuItem key={value} value={value}>{label}</MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    {editForm.source_type === 'youtube_video' && (
                      <Grid size={{ xs: 12 }}>
                        <TextField
                          label="Title / Episode"
                          value={editForm.source_title}
                          onChange={(e) => setEditForm({ ...editForm, source_title: e.target.value })}
                          fullWidth
                          placeholder="e.g. Episode 5 - Seoul Trip"
                        />
                      </Grid>
                    )}
                    <Grid size={{ xs: 12 }}>
                      <TextField
                        label="Source URL"
                        value={editForm.source_url}
                        onChange={(e) => setEditForm({ ...editForm, source_url: e.target.value })}
                        fullWidth
                        placeholder="https://..."
                      />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <TextField
                        label="Notes"
                        value={editForm.notes}
                        onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                        fullWidth
                        multiline
                        rows={2}
                        placeholder="Additional details about the visit..."
                      />
                    </Grid>
                  </Grid>
                  <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<SaveIcon />}
                      onClick={() => saveVisitDetails(visit.id)}
                    >
                      Save
                    </Button>
                    <Button
                      size="small"
                      startIcon={<CloseIcon />}
                      onClick={cancelEditing}
                    >
                      Cancel
                    </Button>
                  </Stack>
                </Box>
              ) : (
                <Box sx={{ ml: 3.5 }}>
                  {(visit.visited_at || visit.source_type || visit.source_title || visit.notes) ? (
                    <Stack spacing={0.5}>
                      {visit.visited_at && (
                        <Typography variant="body2">
                          <Typography component="span" variant="body2" sx={{ fontWeight: 600 }}>Visited:</Typography>{' '}
                          {new Date(visit.visited_at + 'T00:00:00').toLocaleDateString()}
                        </Typography>
                      )}
                      {visit.source_type && (
                        <Typography variant="body2">
                          <Typography component="span" variant="body2" sx={{ fontWeight: 600 }}>Source:</Typography>{' '}
                          {sourceTypeLabels[visit.source_type] || visit.source_type}
                          {visit.source_title && (
                            <> — <Typography component="span" variant="body2" sx={{ fontStyle: 'italic' }}>{visit.source_title}</Typography></>
                          )}
                          {visit.source_url && (
                            <>
                              {' '}
                              <Typography
                                component="a"
                                variant="body2"
                                href={visit.source_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ wordBreak: 'break-all' }}
                              >
                                <OpenInNewIcon sx={{ fontSize: 12, verticalAlign: 'middle' }} />
                              </Typography>
                            </>
                          )}
                        </Typography>
                      )}
                      {visit.notes && (
                        <Typography variant="body2" color="text.secondary">
                          {visit.notes}
                        </Typography>
                      )}
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>
                      No visit details recorded yet. Click edit to add information.
                    </Typography>
                  )}
                </Box>
              )}
            </Paper>
          ))}

          <Divider sx={{ my: 3 }} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            {location.google_place_id ? (
              <Chip label="Google Places" size="small" color="success" variant="outlined" />
            ) : (
              <Chip label="Manual" size="small" variant="outlined" />
            )}
            <Typography variant="caption" color="text.disabled">
              Added {new Date(location.created_at).toLocaleDateString()}
            </Typography>
            {location.author && (
              <Box
                component={Link}
                href={`/users/${location.author.slug || location.author.id}`}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.75,
                  ml: 'auto',
                  px: 1,
                  py: 0.5,
                  borderRadius: 999,
                  textDecoration: 'none',
                  color: 'text.secondary',
                  '&:hover': { backgroundColor: 'rgba(123,91,214,0.08)', color: 'primary.main' },
                }}
                aria-label={`View ${location.author.name}'s profile`}
              >
                <Avatar
                  src={location.author.avatar_url || undefined}
                  alt={location.author.name}
                  sx={{ width: 24, height: 24, fontSize: '0.75rem' }}
                >
                  {location.author.name.charAt(0).toUpperCase()}
                </Avatar>
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  by {location.author.name}
                </Typography>
              </Box>
            )}
          </Box>
        </Paper>
        {signedIn && (
          <WishlistMenu
            anchorEl={wishlistAnchorRef.current}
            open={wishlistMenuOpen}
            onClose={() => { setWishlistMenuOpen(false); router.reload({ only: ['wishlists_summary'] }) }}
            locationId={location.id}
            wishlists={localWishlists}
            onWishlistsChange={(next) => {
              setLocalWishlists(next)
              if (next.some((w) => w.location_ids.includes(location.id))) setWishlisted(true)
            }}
            wishlisted={wishlisted}
            onToggleWishlisted={performToggleWish}
          />
        )}
      </Container>
    </>
  )
}
